import React, { FC, useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  MenuItem,
  Grid,
  InputAdornment,
  CircularProgress,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tooltip,
} from '@mui/material';
import {
  Close as CloseIcon,
  CalendarToday as CalendarTodayIcon,
  Add as AddIcon,
  Save as SaveIcon,
  Delete as DeleteIcon,
  AutoAwesome as AutoAwesomeIcon,
  Science as ScienceIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
} from '@mui/icons-material';
import clientService from 'src/clientService';
import {
  ProjectAdminSummary,
  Site,
  LocationAdminDetail,
  DeploymentAdminDetail,
  SoilElementResponse,
  SoilSampleDetailResponse,
  SoilSampleResultResponse,
  SoilSampleResultCreate,
} from 'src/api/Api';
import { SoilSampleStatus } from './SoilChemistryDeploymentView';
import { useSnackbar, SnackbarType } from 'src/view/components/SnackbarService';
import { SoilElementCreateDialog } from './SoilElementCreateDialog';

interface SoilSampleDialogProps {
  open: boolean;
  onClose: () => void;
  sample?: SoilSampleDetailResponse | null; // If provided, Edit mode
  onSuccess: (sample: SoilSampleDetailResponse) => void;
  initialProjectId?: string;
  initialSiteId?: string;
  initialLocationId?: string;
  initialDeploymentId?: string;
}

const SoilSampleDialog: FC<SoilSampleDialogProps> = ({
  open,
  onClose,
  sample,
  onSuccess,
  initialProjectId,
  initialSiteId,
  initialLocationId,
  initialDeploymentId,
}) => {
  const isEdit = !!sample;
  const { showSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(0);

  // Cascading Location & Deployment Dropdowns
  const [projects, setProjects] = useState<ProjectAdminSummary[]>([]);
  const [sites, setSites] = useState<Site[]>([]);
  const [locations, setLocations] = useState<LocationAdminDetail[]>([]);
  const [deployments, setDeployments] = useState<DeploymentAdminDetail[]>([]);

  // Element Catalog
  const [elementsCatalog, setElementsCatalog] = useState<SoilElementResponse[]>([]);
  const [seedingElements, setSeedingElements] = useState(false);

  // Form State
  const [formData, setFormData] = useState({
    sample_name: '',
    project_id: '',
    site_id: '',
    location_id: '',
    deployment_id: '',
    status: SoilSampleStatus.COLLECTED as string,
    collected_date: '',
    soil_density: '',
    depth: '',
    notes: '',
  });

  // Lab Results State
  const [resultsList, setResultsList] = useState<
    {
      element_id: string;
      value: string;
      unit: string;
      interpretation: string;
    }[]
  >([]);

  const [errors, setErrors] = useState<Record<string, string | null>>({});
  const [isCreateElementDialogOpen, setIsCreateElementDialogOpen] = useState(false);

  const handleCustomElementCreated = async (newElement: SoilElementResponse) => {
    await fetchElementsCatalog();
    setResultsList((prev) => [
      ...prev,
      {
        element_id: newElement.element_id,
        value: '',
        unit: newElement.default_unit || '%',
        interpretation: '',
      },
    ]);
  };

  useEffect(() => {
    if (open) {
      fetchInitialData();
      fetchElementsCatalog();

      if (isEdit && sample) {
        setFormData({
          sample_name: sample.sample_name || '',
          project_id: sample.project_id || '',
          site_id: sample.site_id || '',
          location_id: sample.location_id || '',
          deployment_id: (sample as any).deployment_id || sample.location_id || '',
          status: sample.status || SoilSampleStatus.COLLECTED,
          collected_date: sample.collected_date ? sample.collected_date.split('T')[0] : '',
          soil_density: sample.soil_density !== undefined && sample.soil_density !== null ? String(sample.soil_density) : '',
          depth: sample.depth !== undefined && sample.depth !== null ? String(sample.depth) : '',
          notes: sample.notes || '',
        });

        if (sample.project_id) fetchSites(sample.project_id);
        if (sample.site_id) fetchLocations(sample.site_id);
        if (sample.location_id) fetchDeployments(sample.location_id);

        // Fetch fresh sample details to ensure results are fully loaded
        clientService.api.getSoilSampleApiV1SoilChemistrySamplesSampleIdGet(sample.sample_id)
          .then((response) => {
            const fresh = response.data;
            const resultsToUse = fresh?.results || sample.results || [];
            if (resultsToUse.length > 0) {
              setResultsList(
                resultsToUse.map((r: SoilSampleResultResponse) => ({
                  element_id: r.element_id,
                  value: String(r.value),
                  unit: r.unit || '',
                  interpretation: r.interpretation || '',
                }))
              );
            } else {
              setResultsList([]);
            }
          })
          .catch((err) => {
            console.error('Failed to fetch fresh sample details:', err);
            if (sample.results && sample.results.length > 0) {
              setResultsList(
                sample.results.map((r: SoilSampleResultResponse) => ({
                  element_id: r.element_id,
                  value: String(r.value),
                  unit: r.unit || '',
                  interpretation: r.interpretation || '',
                }))
              );
            } else {
              setResultsList([]);
            }
          });
      } else {
        setFormData({
          sample_name: '',
          project_id: initialProjectId || '',
          site_id: initialSiteId || '',
          location_id: initialLocationId || '',
          deployment_id: initialDeploymentId || '',
          status: SoilSampleStatus.COLLECTED,
          collected_date: new Date().toISOString().split('T')[0],
          soil_density: '',
          depth: '',
          notes: '',
        });
        setResultsList([]);

        if (initialProjectId) fetchSites(initialProjectId);
        if (initialSiteId) fetchLocations(initialSiteId);
        if (initialLocationId) fetchDeployments(initialLocationId);
      }
      setErrors({});
      setActiveTab(0);
    }
  }, [open, sample, isEdit, initialProjectId, initialSiteId, initialLocationId, initialDeploymentId]);

  const fetchInitialData = async () => {
    try {
      const response = await clientService.api.getAllProjectsAdminApiV1AdminProjectsGet();
      setProjects(Array.isArray(response?.data) ? response.data : []);
    } catch (error) {
      console.error('Failed to fetch projects:', error);
      setProjects([]);
    }
  };

  const fetchElementsCatalog = async () => {
    try {
      const response = await clientService.api.listSoilElementsApiV1SoilChemistryElementsGet({ is_active: true });
      let catalog = response.data;
      if (!Array.isArray(catalog) || catalog.length === 0) {
        // Auto-seed default catalog if empty
        const seedResp = await clientService.api.seedSoilElementsApiV1SoilChemistryElementsSeedPost();
        catalog = seedResp.data;
      }
      setElementsCatalog(Array.isArray(catalog) ? catalog : []);
    } catch (error) {
      console.error('Failed to fetch elements catalog:', error);
      setElementsCatalog([]);
    }
  };

  const fetchSites = async (projectId: string) => {
    try {
      const response = await clientService.api.readSitesForProjectApiV1ProjectsProjectIdSitesGet(projectId);
      setSites(Array.isArray(response?.data) ? response.data : []);
    } catch (error) {
      console.error('Failed to fetch sites:', error);
      setSites([]);
    }
  };

  const fetchLocations = async (siteId: string) => {
    try {
      const response = await clientService.api.getSiteDetailsAdminApiV1AdminSitesSiteIdGet(siteId);
      setLocations(Array.isArray(response?.data?.locations) ? response.data.locations : []);
    } catch (error) {
      console.error('Failed to fetch locations:', error);
      setLocations([]);
    }
  };

  const fetchDeployments = async (locationId: string) => {
    try {
      const response = await clientService.api.getDeploymentsByLocationIdApiV1AdminLocationsLocationIdDeploymentsGet(locationId);
      setDeployments(Array.isArray(response?.data) ? response.data : []);
    } catch (error) {
      console.error('Failed to fetch deployments:', error);
      setDeployments([]);
    }
  };

  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: null }));
    }

    if (name === 'project_id' && value) {
      setFormData((prev) => ({ ...prev, site_id: '', location_id: '', deployment_id: '' }));
      setDeployments([]);
      fetchSites(value);
    } else if (name === 'site_id' && value) {
      setFormData((prev) => ({ ...prev, location_id: '', deployment_id: '' }));
      setDeployments([]);
      fetchLocations(value);
    } else if (name === 'location_id' && value) {
      setFormData((prev) => ({ ...prev, deployment_id: '' }));
      fetchDeployments(value);
    }
  };

  // Lab Results Rows Management
  const handleAddResultRow = () => {
    setResultsList((prev) => [
      ...prev,
      { element_id: '', value: '', unit: '', interpretation: '' },
    ]);
  };

  const handleResultRowChange = (index: number, field: string, value: string) => {
    setResultsList((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };

      // Auto fill default unit if element_id is selected
      if (field === 'element_id') {
        const selectedElement = elementsCatalog.find((e) => e.element_id === value);
        if (selectedElement && selectedElement.default_unit) {
          updated[index].unit = selectedElement.default_unit;
        }
      }
      return updated;
    });
  };

  const handleRemoveResultRow = (index: number) => {
    setResultsList((prev) => prev.filter((_, i) => i !== index));
  };

  const handleLoadStandardElements = async () => {
    try {
      setSeedingElements(true);
      let catalog = elementsCatalog;
      if (!Array.isArray(catalog) || catalog.length === 0) {
        const seedResp = await clientService.api.seedSoilElementsApiV1SoilChemistryElementsSeedPost();
        catalog = seedResp.data;
        setElementsCatalog(Array.isArray(catalog) ? catalog : []);
      }

      // Pre-fill top standard elements if not already in list
      const existingElementIds = new Set(resultsList.map((r) => r.element_id));
      const defaultRows = catalog
        .filter((e) => !existingElementIds.has(e.element_id))
        .map((e) => ({
          element_id: e.element_id,
          value: '',
          unit: e.default_unit || '',
          interpretation: 'Optimal',
        }));

      setResultsList((prev) => [...prev, ...defaultRows]);
      showSnackbar({ message: 'Loaded standard soil elements catalog into table', type: SnackbarType.SUCCESS });
    } catch (error) {
      console.error('Failed to seed catalog elements:', error);
      showSnackbar({ message: 'Failed to load element catalog', type: SnackbarType.ERROR });
    } finally {
      setSeedingElements(false);
    }
  };

  const validate = () => {
    const newErrors: Record<string, string> = {};
    if (!formData.sample_name.trim()) newErrors.sample_name = 'Sample Name is required';
    if (!isEdit) {
      if (!formData.project_id) newErrors.project_id = 'Project is required';
      if (!formData.site_id) newErrors.site_id = 'Site is required';
      if (!formData.location_id) newErrors.location_id = 'Location is required';
      if (!formData.deployment_id) newErrors.deployment_id = 'Deployment is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) {
      setActiveTab(0);
      return;
    }

    setLoading(true);
    try {
      // Build results payload
      const validResults: SoilSampleResultCreate[] = resultsList
        .filter((r) => r.element_id && r.value !== '' && !isNaN(Number(r.value)))
        .map((r) => ({
          element_id: r.element_id,
          value: Number(r.value),
          unit: r.unit || undefined,
          interpretation: r.interpretation || undefined,
        }));

      const formattedDate = formData.collected_date && formData.collected_date.trim() !== ''
        ? new Date(formData.collected_date).toISOString()
        : undefined;

      if (isEdit && sample) {
        // Update Sample Details
        await clientService.api.updateSoilSampleApiV1SoilChemistrySamplesSampleIdPut(sample.sample_id, {
          sample_name: formData.sample_name,
          status: formData.status,
          collected_date: formattedDate,
          soil_density: formData.soil_density !== '' ? Number(formData.soil_density) : undefined,
          depth: formData.depth !== '' ? Number(formData.depth) : undefined,
          notes: formData.notes || undefined,
        });

        // Synchronize results (add, update, or delete removed elements)
        await clientService.api.bulkCreateSampleResultsApiV1SoilChemistrySamplesSampleIdResultsBulkPost(sample.sample_id, {
          results: validResults,
        });

        const freshResponse = await clientService.api.getSoilSampleApiV1SoilChemistrySamplesSampleIdGet(sample.sample_id);
        onSuccess(freshResponse.data);
        showSnackbar({ message: 'Soil sample updated successfully!', type: SnackbarType.SUCCESS });
      } else {
        // Create Sample with nested results
        const createResp = await clientService.api.createSoilSampleApiV1SoilChemistrySamplesPost({
          deployment_id: formData.deployment_id || formData.location_id,
          location_id: formData.location_id || formData.deployment_id,
          sample_name: formData.sample_name,
          status: formData.status,
          collected_date: formattedDate,
          soil_density: formData.soil_density !== '' ? Number(formData.soil_density) : undefined,
          depth: formData.depth !== '' ? Number(formData.depth) : undefined,
          notes: formData.notes || undefined,
          results: validResults.length > 0 ? validResults : undefined,
        });

        onSuccess(createResp.data);
        showSnackbar({ message: 'Soil sample created successfully!', type: SnackbarType.SUCCESS });
      }
      onClose();
    } catch (error) {
      console.error('Failed to save soil sample:', error);
      showSnackbar({
        message: `Failed to ${isEdit ? 'update' : 'create'} soil sample`,
        type: SnackbarType.ERROR,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
    >
      {/* Header */}
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <ScienceIcon color="primary" sx={{ fontSize: 28 }} />
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              {isEdit ? 'Edit Soil Sample' : 'Create New Soil Sample'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {isEdit
                ? 'Update sample properties and lab chemical results.'
                : 'Enter field collection metadata and laboratory chemical measurements.'}
            </Typography>
          </Box>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 3 }}>
        <Tabs
          value={activeTab}
          onChange={(_, val) => setActiveTab(val)}
          indicatorColor="primary"
          textColor="primary"
        >
          <Tab label="1. Collection Metadata" />
          <Tab label={`2. Lab Chemical Results (${resultsList.length})`} />
        </Tabs>
      </Box>

      <DialogContent dividers sx={{ maxHeight: '65vh' }}>
        {/* TAB 0: METADATA */}
        {activeTab === 0 && (
          <Box sx={{ pt: 1 }}>
            {!isEdit && !initialDeploymentId && (
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    select
                    fullWidth
                    label="Project *"
                    name="project_id"
                    value={formData.project_id}
                    onChange={handleFormChange}
                    error={!!errors.project_id}
                    helperText={errors.project_id}
                    disabled={!!initialProjectId}
                    SelectProps={{ IconComponent: KeyboardArrowDownIcon }}
                  >
                    {projects.map((p) => (
                      <MenuItem key={p.project_id} value={p.project_id}>
                        {p.project_name}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    select
                    fullWidth
                    label="Site *"
                    name="site_id"
                    value={formData.site_id}
                    onChange={handleFormChange}
                    error={!!errors.site_id}
                    helperText={errors.site_id}
                    disabled={!formData.project_id || !!initialSiteId}
                    SelectProps={{ IconComponent: KeyboardArrowDownIcon }}
                  >
                    {sites.map((s) => (
                      <MenuItem key={s.site_id} value={s.site_id}>
                        {s.site_name}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    select
                    fullWidth
                    label="Location *"
                    name="location_id"
                    value={formData.location_id}
                    onChange={handleFormChange}
                    error={!!errors.location_id}
                    helperText={errors.location_id}
                    disabled={!formData.site_id || !!initialLocationId}
                    SelectProps={{ IconComponent: KeyboardArrowDownIcon }}
                  >
                    {locations.map((l) => (
                      <MenuItem key={l.location_id} value={l.location_id}>
                        {l.location_name}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    select
                    fullWidth
                    label="Deployment *"
                    name="deployment_id"
                    value={formData.deployment_id}
                    onChange={handleFormChange}
                    error={!!errors.deployment_id}
                    helperText={errors.deployment_id || (deployments.length === 0 && formData.location_id ? 'No deployments found' : undefined)}
                    disabled={!formData.location_id || !!initialDeploymentId}
                    SelectProps={{ IconComponent: KeyboardArrowDownIcon }}
                  >
                    {deployments.map((d) => (
                      <MenuItem key={d.deployment_id} value={d.deployment_id}>
                        {d.deployment_name} {d.device?.type ? `(${d.device.type})` : ''}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
              </Grid>
            )}

            <Box sx={{ mb: 3 }}>
              <TextField
                fullWidth
                label="Sample Name *"
                name="sample_name"
                placeholder="e.g. PlotA_Core_15cm"
                value={formData.sample_name}
                onChange={handleFormChange}
                error={!!errors.sample_name}
                helperText={errors.sample_name}
              />
            </Box>

            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  type="date"
                  label="Collected Date"
                  name="collected_date"
                  value={formData.collected_date}
                  onChange={handleFormChange}
                  InputLabelProps={{ shrink: true }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <CalendarTodayIcon color="action" sx={{ fontSize: 18 }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  type="number"
                  label="Soil Density (g/cm³)"
                  name="soil_density"
                  placeholder="1.35"
                  value={formData.soil_density}
                  onChange={handleFormChange}
                  inputProps={{ step: '0.01' }}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  type="number"
                  label="Sample Depth (cm)"
                  name="depth"
                  placeholder="15.0"
                  value={formData.depth}
                  onChange={handleFormChange}
                  inputProps={{ step: '0.5' }}
                />
              </Grid>
            </Grid>

            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} md={6}>
                <TextField
                  select
                  fullWidth
                  label="Sample Status"
                  name="status"
                  value={formData.status}
                  onChange={handleFormChange}
                  SelectProps={{ IconComponent: KeyboardArrowDownIcon }}
                >
                  {Object.values(SoilSampleStatus).map((st) => (
                    <MenuItem key={st} value={st}>
                      {st}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Field Notes & Metadata"
                  name="notes"
                  placeholder="Additional observations, weather conditions, sampler info..."
                  value={formData.notes}
                  onChange={handleFormChange}
                />
              </Grid>
            </Grid>
          </Box>
        )}

        {/* TAB 1: LAB CHEMICAL RESULTS */}
        {activeTab === 1 && (
          <Box sx={{ pt: 1 }}>
            <Paper
              variant="outlined"
              sx={{
                display: 'flex',
                justify: 'space-between',
                alignItems: 'center',
                mb: 2,
                p: 2,
              }}
            >
              <Typography variant="body2" color="text.secondary">
                Add chemical measurement values collected from soil laboratory testing.
              </Typography>
              <Box sx={{ display: 'flex', gap: 1.5 }}>
                <Button
                  size="small"
                  variant="outlined"
                  color="secondary"
                  onClick={() => setIsCreateElementDialogOpen(true)}
                  startIcon={<ScienceIcon />}
                >
                  Create Custom Element
                </Button>

                <Button
                  size="small"
                  variant="outlined"
                  color="primary"
                  onClick={handleLoadStandardElements}
                  disabled={seedingElements}
                  startIcon={<AutoAwesomeIcon />}
                >
                  Load Standard Catalog
                </Button>

                <Button
                  size="small"
                  variant="outlined"
                  onClick={handleAddResultRow}
                  startIcon={<AddIcon />}
                >
                  Add Row
                </Button>
              </Box>
            </Paper>

            {resultsList.length === 0 ? (
              <Paper
                variant="outlined"
                sx={{
                  textAlign: 'center',
                  py: 6,
                  px: 2,
                  borderStyle: 'dashed',
                }}
              >
                <ScienceIcon sx={{ fontSize: 40, color: 'text.secondary', mb: 1 }} />
                <Typography variant="body1" color="text.secondary" sx={{ fontWeight: 600 }}>
                  No chemical results added yet
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                  Click "Load Standard Catalog" to automatically populate key soil parameters like SOC, pH, N, P, K.
                </Typography>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleLoadStandardElements}
                  startIcon={<AutoAwesomeIcon />}
                >
                  Load Standard Catalog
                </Button>
              </Paper>
            ) : (
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell width="35%" sx={{ fontWeight: 700 }}>Chemical Element</TableCell>
                      <TableCell width="20%" sx={{ fontWeight: 700 }}>Value</TableCell>
                      <TableCell width="15%" sx={{ fontWeight: 700 }}>Unit</TableCell>
                      <TableCell width="22%" sx={{ fontWeight: 700 }}>Interpretation</TableCell>
                      <TableCell width="8%" align="center" sx={{ fontWeight: 700 }}>
                        Action
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {resultsList.map((row, idx) => (
                      <TableRow key={idx}>
                        <TableCell>
                          <TextField
                            select
                            fullWidth
                            size="small"
                            value={row.element_id}
                            onChange={(e) => handleResultRowChange(idx, 'element_id', e.target.value)}
                            SelectProps={{ IconComponent: KeyboardArrowDownIcon }}
                          >
                            {elementsCatalog.map((el) => (
                              <MenuItem key={el.element_id} value={el.element_id}>
                                {el.name} ({el.symbol})
                              </MenuItem>
                            ))}
                          </TextField>
                        </TableCell>
                        <TableCell>
                          <TextField
                            fullWidth
                            size="small"
                            type="number"
                            placeholder="0.00"
                            value={row.value}
                            onChange={(e) => handleResultRowChange(idx, 'value', e.target.value)}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            fullWidth
                            size="small"
                            placeholder="%"
                            value={row.unit}
                            onChange={(e) => handleResultRowChange(idx, 'unit', e.target.value)}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            select
                            fullWidth
                            size="small"
                            value={row.interpretation}
                            onChange={(e) => handleResultRowChange(idx, 'interpretation', e.target.value)}
                            SelectProps={{ IconComponent: KeyboardArrowDownIcon }}
                          >
                            <MenuItem value="Optimal">Optimal</MenuItem>
                            <MenuItem value="Deficient">Deficient</MenuItem>
                            <MenuItem value="Excessive">Excessive</MenuItem>
                            <MenuItem value="Moderate">Moderate</MenuItem>
                          </TextField>
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title="Remove row">
                            <IconButton size="small" onClick={() => handleRemoveResultRow(idx)} color="error">
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        )}
      </DialogContent>

      {/* Footer */}
      <DialogActions sx={{ p: 2.5, justifyContent: 'space-between' }}>
        <Button onClick={onClose} color="inherit">
          Cancel
        </Button>
        <Box sx={{ display: 'flex', gap: 2 }}>
          {activeTab === 0 ? (
            <Button
              variant="outlined"
              onClick={() => setActiveTab(1)}
            >
              Next: Lab Results →
            </Button>
          ) : (
            <Button
              variant="outlined"
              onClick={() => setActiveTab(0)}
            >
              ← Back to Metadata
            </Button>
          )}

          <Button
            variant="contained"
            color="primary"
            onClick={handleSubmit}
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : isEdit ? <SaveIcon /> : <AddIcon />}
          >
            {isEdit ? 'Save Changes' : 'Create Sample'}
          </Button>
        </Box>
      </DialogActions>
      <SoilElementCreateDialog
        open={isCreateElementDialogOpen}
        onClose={() => setIsCreateElementDialogOpen(false)}
        onSuccess={handleCustomElementCreated}
      />
    </Dialog>
  );
};

export default SoilSampleDialog;
